import { errorNotification, successNotification } from "app/utils/Notification";
import { get, isEmpty } from "lodash";
import { rApp, rSetConfig } from "app/utils/recoil";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { AdminForm } from "../components";
import { Modal } from "app/components";
import { apiRequest } from "app/utils/apiRequests";
import { decryptConfig } from "app/utils/encryptionUtils";
import { isValidSubdomain } from "app/utils/utils";
import mixpanel from "mixpanel-browser";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const CreateAppModal = ({ type, hide }) => {
  const setConfig = useSetRecoilState(rSetConfig);

  const [isFetching, setIsFetching] = useState(false);
  const [errors, errorsSet] = useState({});

  const app = useRecoilValue(rApp);

  const hasSupabase = !!get(app, "supabase_project_id");
  const hasGoogle = get(app, "google_sheets_connected");
  const hasAirtable = !!get(app, "airtable_token");

  const hasDataIntegrations = hasSupabase || hasGoogle || hasAirtable;

  const [formData, setFormData] = useState({
    name: "New App",
    subdomain: "",
    copy_data_integrations: hasDataIntegrations,
    ai_prompt: "",
  });

  const navigate = useNavigate();

  const { name, subdomain, ai_prompt } = formData;

  const copyDataIntegrations = get(formData, "copy_data_integrations");

  const isFormValid = () => {
    let errors = {};

    if (!name) {
      errors["name"] = "Please enter a valid name.";
    } else if (name.length < 3) {
      errors["name"] = "Please enter a longer name.";
    }

    errors = { ...errors, ...isValidSubdomain(subdomain) };
    if (type === "ai" && (ai_prompt.trim().length < 10 || ai_prompt.length > 8000)) errors.ai_prompt = "Describe your app in 10 to 8,000 characters.";

    errorsSet(errors);

    return isEmpty(errors);
  };

  const createApp = () => {
    if (!isFormValid()) return;

    setIsFetching(true);
    apiRequest.post("/create_app/", {
      name, subdomain, copy_data_integrations: copyDataIntegrations,
      ...(type === "ai" ? { ai_prompt } : {}),
    }, { timeout: type === "ai" ? 120000 : 20000 })
      .then((response) => {
        const responseData = get(response, "data");
        if (responseData?.error) {
          errorNotification(responseData.error);
          return;
        }
        const data = decryptConfig(responseData);
        setConfig(data);
        localStorage.setItem("appId", get(data, ["app", "id"]));
        successNotification(type === "ai" ? "AI app created" : "App Created");
        mixpanel.track("App Created", { template: type === "ai" ? "ai" : "blank" });
        hide();
        navigate("/pages");
      })
      .catch((error) => errorNotification(get(error, "response.data.error", "App creation failed. Please try again.")))
      .finally(() => setIsFetching(false));
  };

  let fields = [
    {
      id: "name",
      componentId: "Input",
      label: "Name",
      value: name,
    },
    {
      id: "subdomain",
      componentId: "Input",
      label: "Subdomain",
      value: subdomain,
      hint: "A subdomain is the web address for your frontly-branded domain, like myappname.frontly.ai. This can be changed from settings at any time.",
    },
  ];

  // Add copy data integrations field if there are data integrations
  if (hasDataIntegrations) {
    fields = [
      ...fields,
      {
        id: "copy_data_integrations",
        componentId: "Switch",
        label: "Copy Existing Data Integrations",
        hint: "If enabled, we'll copy your current app's data integration connections to the new app.",
        value: copyDataIntegrations,
      },
    ];
  }

  // Add AI Prompt field
  if (type === "ai") {
    fields = [
      ...fields,
      {
        id: "ai_prompt",
        componentId: "TextArea",
        label: "Describe the app you want to create",
        minHeight: "100px",
        value: ai_prompt,
        hint: "This prompt will be used to generate the app.",
      },
    ];


  }

  return (
    <Modal
      minWidth={type === "ai" ? "550px" : "400px"}
      header={{
        title:
          type === "ai" ? "Generate a starter app with AI" : "Create New App",
        description:
          type === "ai"
            ? "We'll generate some pages and data tables to get you started"
            : "",
      }}
      hide={hide}
    >
      <AdminForm
        isFetching={isFetching}
        labelStyle="headingSm"
        submit={createApp}
        errors={errors}
        submitText={type === "ai" ? "Generate Starter App" : "Create App"}
        onChange={(k, v) =>
          setFormData((current) => ({
            ...current,
            [k]: v,
          }))
        }
        sectionPadding="0px"
        fields={fields}
      />
    </Modal>
  );
};

export default CreateAppModal;
